/**
 * Media Utilities for Creatives Module
 * Consolidates common media processing, validation, and manipulation functions
 */

import { Storage, Auth } from "aws-amplify";
import { message } from "antd";
import aws_exports from "../../../../../aws-exports";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";

/**
 * Transforms filename to be S3-compatible
 * @param {string} originalFileName - Original file name
 * @returns {string} - Transformed filename
 */
export const transformFileName = originalFileName => {
  const fileParts = originalFileName.split(".");
  const fileExtension = fileParts.pop();
  const fileNameWithoutExtension = fileParts.join(".");

  // Remove spaces and replace with underscore
  let fileNameWithoutSpaces = fileNameWithoutExtension.replace(/\s+/g, "_");

  // Remove non-alphanumeric characters
  let alphanumericFileName = fileNameWithoutSpaces.replace(
    /[^a-zA-Z0-9_]/g,
    ""
  );

  // Convert to lowercase
  alphanumericFileName = alphanumericFileName.toLowerCase();

  // Add a date stamp in Unix format with milliseconds
  const currentDate = new Date();
  const unixTimestamp = currentDate.getTime();
  const formattedFileName = `${alphanumericFileName}_${unixTimestamp}.${fileExtension}`;

  return formattedFileName;
};


/**
 * Gets filename without extension
 * @param {string} fileName - File name with extension
 * @returns {string} - Filename without extension
 */
export const getFileNameWithoutExtension = fileName => {
  const parts = fileName.split(".");
  parts.pop();
  return parts.join(".");
};

/**
 * Direct S3 upload using AWS SDK (bypasses Amplify's hardcoded 5MB multipart threshold)
 * @param {File} file - File to upload
 * @param {string} fileName - Transformed filename
 * @param {string} contentType - Content type of the file
 * @returns {Promise<{key: string}>} - Upload response with S3 key
 */
const directS3Upload = async (file, fileName, contentType) => {
  try {
    // Get AWS credentials from Amplify Auth
    const credentials = await Auth.currentCredentials();

    // Create S3 client with credentials
    const s3Client = new S3Client({
      region: aws_exports.aws_user_files_s3_bucket_region,
      credentials: Auth.essentialCredentials(credentials),
    });

    // Prepare S3 key (same format as Amplify Storage)
    const s3Key = `public/${fileName}`;

    // Read file as ArrayBuffer
    const fileBuffer = await file.arrayBuffer();

    // Execute direct PUT (not multipart)
    const command = new PutObjectCommand({
      Bucket: aws_exports.aws_user_files_s3_bucket,
      Key: s3Key,
      Body: fileBuffer,
      ContentType: contentType,
      ContentDisposition: `inline; filename="${fileName}"`,
    });

    await s3Client.send(command);

    return { key: fileName }; // Return key in Amplify format
  } catch (error) {
    console.error('Direct S3 upload failed:', error);
    throw error;
  }
};

/**
 * Uploads file to S3 storage with size validation
 * @param {File} file - File to upload
 * @param {string} contentType - Content type of the file
 * @returns {Promise<{url: string, key: string}>} - Upload response
 */
export const uploadToS3Storage = async (file, contentType = "image/jpeg") => {
  const fileName = transformFileName(file.name);

  // Maximum file size for uploads (matches Nexxen CTV specs: 5MB-30MB recommended)
  // Uses direct AWS SDK upload for files >5MB to bypass Amplify's hardcoded multipart threshold
  const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

  if (file.size > MAX_FILE_SIZE) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    const errorMessage = `File size (${fileSizeMB}MB) exceeds the 30MB limit. Please compress your video to under 30MB with a bitrate of 1000-3000 kbps.`;
    message.error(errorMessage, 10);
    throw new Error(errorMessage);
  }

  // For video files, validate bitrate before upload
  if (contentType.includes('video') || file.type.includes('video')) {
    try {
      const metadata = await getVideoMetadata(file);
      const bitrateValidation = validateVideoBitrate(metadata.bitrate);

      // Enforce maximum bitrate limit
      if (!bitrateValidation.isValid) {
        message.error(bitrateValidation.message, 10);
        throw new Error(bitrateValidation.message);
      } else if (metadata.bitrate > 4000) {
        message.warning(bitrateValidation.message, 6);
      }
    } catch (metadataError) {
      // If it's our validation error, re-throw it
      if (metadataError.message?.includes('bitrate')) {
        throw metadataError;
      }
      console.warn('Could not validate video bitrate:', metadataError);
      // Continue with upload if metadata extraction fails
    }
  }

  try {
    // AWS Amplify v4.3.x has HARDCODED 5MB multipart threshold that cannot be configured
    // For files >5MB, use direct AWS SDK upload to bypass Amplify's multipart logic
    // For files ≤5MB, use Amplify Storage.put() (works fine without multipart)
    const USE_DIRECT_SDK_THRESHOLD = 5 * 1024 * 1024; // 5MB - Amplify's hardcoded multipart threshold
    const useDirectSDK = file.size > USE_DIRECT_SDK_THRESHOLD;

    let response;

    if (useDirectSDK) {
      // Use direct AWS SDK for files >5MB to avoid Amplify's hardcoded multipart
      response = await directS3Upload(file, fileName, contentType);
    } else {
      // Use Amplify Storage for files ≤5MB (no multipart triggered)
      const uploadConfig = {
        contentType: contentType,
        level: 'public',
      };

      response = await Storage.put(fileName, file, uploadConfig);
    }

    const url = await Storage.get(response.key, {
      expires: 600 // 10 minutes in SECONDS (not milliseconds)
    });

    return {
      url: url,
      key: response.key,
    };

  } catch (error) {
    console.error('Upload failed:', error);

    // Enhanced error handling with solution suggestions
    let errorMessage = "Upload failed";

    if (error.message?.includes('timeout')) {
      errorMessage = "Upload timed out. Please check your internet connection and try again.";
    } else if (error.message?.includes('network')) {
      errorMessage = "Network error during upload. Please try again.";
    } else {
      errorMessage = `Upload failed: ${error.message}`;
    }

    message.error(errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Gets video duration from file
 * @param {File} file - Video file
 * @returns {Promise<number>} - Video duration in seconds
 */
export const getVideoDuration = file => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(0);
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.src = URL.createObjectURL(file);

    videoElement.onloadedmetadata = function() {
      URL.revokeObjectURL(videoElement.src);
      resolve(videoElement.duration);
    };

    videoElement.onerror = function() {
      URL.revokeObjectURL(videoElement.src);
      reject(new Error("Failed to load video metadata"));
    };
  });
};

/**
 * Gets comprehensive video metadata from file
 * @param {File} file - Video file
 * @returns {Promise<{width: number, height: number, duration: number, bitrate: number, aspectRatio: string, aspectRatioDecimal: number, hasIssues: boolean, issues: string[]}>} - Video metadata
 */
export const getVideoMetadata = file => {
  return new Promise(resolve => {
    if (!file) {
      resolve({
        width: 0,
        height: 0,
        duration: 0,
        bitrate: 0,
        aspectRatio: '0:0',
        aspectRatioDecimal: 0,
        hasIssues: false,
        issues: []
      });
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.src = URL.createObjectURL(file);

    videoElement.onloadedmetadata = function() {
      const duration = videoElement.duration;
      const dimensions = {
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      };

      // Calculate approximate bitrate (bits per second)
      const bitrate = duration > 0 ? (file.size * 8) / duration : 0;
      const bitrateKbps = bitrate / 1000;

      // Calculate aspect ratio
      const aspectRatio = dimensions.width / dimensions.height;
      const aspectRatioString = `${dimensions.width}:${dimensions.height}`;

      // Check for potential issues
      const issues = [];
      let hasIssues = false;

      // File size check (max 30MB)
      if (file.size > 30 * 1024 * 1024) {
        issues.push(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 30MB)`);
        hasIssues = true;
      }

      // Bitrate validation - Platform requirements: 800-5000 kbps (optimal: 1000-3000)
      const MIN_REQUIRED_BITRATE = 800;
      const MAX_REQUIRED_BITRATE = 5000;
      const OPTIMAL_MAX_BITRATE = 3000;

      if (bitrateKbps < MIN_REQUIRED_BITRATE) {
        issues.push(`❌ Bitrate too low: ${Math.round(bitrateKbps)} kbps. Minimum ${MIN_REQUIRED_BITRATE} kbps required for acceptable quality.`);
        hasIssues = true;
      } else if (bitrateKbps > MAX_REQUIRED_BITRATE) {
        issues.push(`❌ Bitrate too high: ${Math.round(bitrateKbps)} kbps exceeds maximum ${MAX_REQUIRED_BITRATE} kbps. Please compress your video.`);
        hasIssues = true;
      } else if (bitrateKbps > OPTIMAL_MAX_BITRATE) {
        issues.push(`⚠️ High bitrate: ${Math.round(bitrateKbps)} kbps (optimal range: 1000-3000 kbps)`);
      }

      // Aspect ratio validation - Platform accepts: 16:9 or 4:3 (CTV requires 16:9)
      const is16by9 = Math.abs(aspectRatio - (16/9)) < 0.05;
      const is4by3 = Math.abs(aspectRatio - (4/3)) < 0.05;

      if (!is16by9 && !is4by3) {
        issues.push(`❌ Aspect ratio ${aspectRatio.toFixed(2)} not supported. Required: 16:9 or 4:3.`);
        hasIssues = true;
      } else if (is4by3) {
        issues.push(`⚠️ 4:3 aspect ratio detected. Note: CTV only accepts 16:9 format.`);
      }

      // Resolution check - Preferred: 1920×1080 or 1280×720
      if (dimensions.width > 1920 || dimensions.height > 1080) {
        issues.push(`⚠️ High resolution: ${dimensions.width}x${dimensions.height} (preferred: 1920×1080 or 1280×720)`);
      }

      URL.revokeObjectURL(videoElement.src);
      resolve({
        width: dimensions.width,
        height: dimensions.height,
        duration,
        bitrate: bitrateKbps,
        aspectRatio: aspectRatioString,
        aspectRatioDecimal: aspectRatio,
        hasIssues,
        issues
      });
    };

    videoElement.onerror = function() {
      URL.revokeObjectURL(videoElement.src);
      resolve({
        width: 0,
        height: 0,
        duration: 0,
        bitrate: 0,
        aspectRatio: '0:0',
        aspectRatioDecimal: 0,
        hasIssues: true,
        issues: ['Failed to load video metadata - file may be corrupted']
      });
    };
  });
};

/**
 * Gets video dimensions from file (legacy compatibility)
 * @param {File} file - Video file
 * @returns {Promise<{width: number, height: number}>} - Video dimensions
 */
export const getVideoDimensions = async file => {
  const metadata = await getVideoMetadata(file);
  return {
    width: metadata.width,
    height: metadata.height,
  };
};

/**
 * Gets image dimensions from file
 * @param {File} file - Image file
 * @returns {Promise<{width: number, height: number}>} - Image dimensions
 */
export const getImageDimensions = file => {
  return new Promise(resolve => {
    if (!file) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = function() {
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      URL.revokeObjectURL(img.src);
      resolve(dimensions);
    };

    img.onerror = function() {
      URL.revokeObjectURL(img.src);
      resolve({ width: 0, height: 0 });
    };
  });
};

/**
 * Validates file size with clear messaging
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default 30MB)
 * @returns {boolean} - Whether file size is valid
 */
export const validateFileSize = (file, maxSizeMB = 30) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    message.error(`File size (${fileSizeMB}MB) exceeds the ${maxSizeMB}MB limit. Please compress your file.`);
    return false;
  }
  return true;
};

/**
 * Validates image dimensions against allowed dimensions
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {Array} validDimensions - Array of valid dimension objects
 * @returns {boolean} - Whether dimensions are valid
 */
export const validateImageDimensions = (
  width,
  height,
  validDimensions = []
) => {
  if (!validDimensions.length) return true;

  return validDimensions.some(
    dim => dim.width === width && dim.height === height
  );
};

/**
 * Validates video duration
 * @param {number} duration - Duration in seconds
 * @param {number} minDuration - Minimum duration in seconds
 * @param {number} maxDuration - Maximum duration in seconds
 * @returns {boolean} - Whether duration is valid
 */
export const validateVideoDuration = (
  duration,
  minDuration = 15,
  maxDuration = 30
) => {
  return duration >= minDuration && duration <= maxDuration;
};

/**
 * Validates video bitrate against platform requirements
 * @param {number} bitrateKbps - Video bitrate in kbps
 * @param {number} minBitrateKbps - Minimum allowed bitrate (default: 800 kbps)
 * @param {number} maxBitrateKbps - Maximum allowed bitrate (default: 5000 kbps)
 * @returns {{isValid: boolean, message: string}} - Validation result
 */
export const validateVideoBitrate = (bitrateKbps, minBitrateKbps = 800, maxBitrateKbps = 5000) => {
  if (bitrateKbps < minBitrateKbps) {
    return {
      isValid: false,
      message: `Video bitrate ${Math.round(bitrateKbps)} kbps is below minimum of ${minBitrateKbps} kbps. Quality may be too low.`
    };
  }

  if (bitrateKbps > maxBitrateKbps) {
    return {
      isValid: false,
      message: `Video bitrate ${Math.round(bitrateKbps)} kbps exceeds the maximum allowed ${maxBitrateKbps} kbps. Please compress your video using tools like HandBrake or FFmpeg to reduce the bitrate to 1000-3000 kbps (recommended for best compatibility).`
    };
  }

  if (bitrateKbps > 4000) {
    return {
      isValid: true,
      message: `Video bitrate ${Math.round(bitrateKbps)} kbps is acceptable for CTV (1000-5000 kbps).`
    };
  }

  return {
    isValid: true,
    message: `Video bitrate ${Math.round(bitrateKbps)} kbps is optimal for advertising platforms.`
  };
};

/**
 * Processes media files by renewing S3 URLs
 * @param {Array} medias - Array of media objects
 * @returns {Promise<Array>} - Processed media array
 */
export const processMediasWithUrls = async medias => {
  if (!medias || !Array.isArray(medias)) return [];

  const processedMedias = await Promise.all(
    medias.map(async media => {
      try {
        const response = await Storage.get(media.key, { expires: 600 });
        return {
          ...media,
          url: response,
        };
      } catch (error) {
        return media;
      }
    })
  );

  return processedMedias;
};

/**
 * Filters ad groups by media type
 * @param {Array} adGroups - Array of ad group objects
 * @param {string} mediaType - Media type to filter by ("VIDEO" or other)
 * @returns {Array} - Filtered ad groups
 */
export const filterAdGroupsByMediaType = (adGroups, mediaType) => {
  if (!adGroups || !Array.isArray(adGroups)) return [];

  return adGroups.filter(group => {
    if (!Array.isArray(group.medias)) return false;

    if (mediaType === "VIDEO") {
      return group.medias.some(
        media => media.type && media.type.toUpperCase() === "VIDEO"
      );
    } else {
      return group.medias.some(
        media => media.type && media.type.toUpperCase() !== "VIDEO"
      );
    }
  });
};

/**
 * Filters ad groups by media type compatibility for assignment
 * Returns ad groups that can accept the specified media type without conflicts
 * @param {Array} adGroups - Array of ad group objects
 * @param {string} mediaType - Media type to check compatibility for ("VIDEO" or other)
 * @returns {Array} - Filtered ad groups that are compatible with the media type
 */
export const filterAdGroupsByMediaCompatibility = (adGroups, mediaType) => {
  if (!adGroups || !Array.isArray(adGroups)) return [];

  return adGroups.filter(group => {
    // Empty ad groups (no medias array or empty array) can accept any media type
    if (!Array.isArray(group.medias) || group.medias.length === 0) {
      return true;
    }

    if (mediaType === "VIDEO") {
      // For video: show ad groups that don't contain non-video media
      return !group.medias.some(
        media =>
          (media?.type && media.type.toUpperCase() !== "VIDEO") ||
          (media?.fileType && media.fileType.toUpperCase() !== "MP4")
      );
    } else {
      // For display/image: show ad groups that don't contain video media
      return !group.medias.some(
        media =>
          (media?.type && media.type.toUpperCase() === "VIDEO") ||
          (media?.fileType && media.fileType.toUpperCase() === "MP4")
      );
    }
  });
};

/**
 * Capitalizes first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
export const capitalizeFirstLetter = str => {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = bytes => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Common error handler for media operations
 * @param {Error} error - Error object
 * @param {string} operation - Operation that failed
 * @param {string} defaultMessage - Default error message
 */
export const handleMediaError = (error, operation, defaultMessage) => {
  if (error.message) {
    message.error(`${operation} failed: ${error.message}`);
  } else {
    message.error(defaultMessage || `${operation} failed`);
  }
};

/**
 * Common success handler for media operations
 * @param {string} operation - Operation that succeeded
 * @param {number} count - Number of items processed
 */
export const handleMediaSuccess = (operation, count = 1) => {
  const itemText = count === 1 ? "item" : "items";
  message.success(
    `${operation} completed successfully! ${count} ${itemText} processed.`
  );
};

/**
 * Debounce function for search operations
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

/**
 * Standardized search function for creative components
 * @param {Array} items - Array of items to search
 * @param {string} searchText - Search query
 * @param {string} itemType - Type of item ('media', 'adGroup')
 * @returns {Array} - Filtered items
 */
export const performCreativeSearch = (items, searchText, itemType = 'media') => {
  if (!searchText || !items || !Array.isArray(items)) {
    return items || [];
  }

  const searchWords = searchText.toLowerCase().split(" ").filter(word => word.trim());

  return items.filter(item => {
    if (!item) return false;

    const searchFields = [];

    if (itemType === 'media') {
      // For individual media items
      searchFields.push(
        item.name?.toLowerCase() || '',
        item.fileType?.toLowerCase() || '',
        item.type?.toLowerCase() || '',
        `${item.width || 0}x${item.height || 0}`,
        formatFileSize(item.size || 0).toLowerCase()
      );

      // Add date fields for media
      if (item.createdAt) {
        const date = new Date(item.createdAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(), // MM/DD/YYYY format
          date.getFullYear().toString(), // Year
          (date.getMonth() + 1).toString().padStart(2, '0'), // Month (01-12)
          date.getDate().toString().padStart(2, '0') // Day (01-31)
        );
      }
      if (item.updatedAt) {
        const date = new Date(item.updatedAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }
    } else if (itemType === 'adGroup') {
      // For ad groups
      searchFields.push(item.name?.toLowerCase() || '');

      // Add date fields for ad groups
      if (item.createdAt) {
        const date = new Date(item.createdAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }
      if (item.updatedAt) {
        const date = new Date(item.updatedAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }

      // Add media names and details from the ad group
      if (item.medias && Array.isArray(item.medias)) {
        item.medias.forEach(media => {
          searchFields.push(
            media.name?.toLowerCase() || '',
            media.fileType?.toLowerCase() || '',
            media.type?.toLowerCase() || '',
            `${media.width || 0}x${media.height || 0}`,
            formatFileSize(media.size || 0).toLowerCase()
          );

          // Add media dates
          if (media.createdAt) {
            const date = new Date(media.createdAt);
            searchFields.push(
              date.toLocaleDateString().toLowerCase(),
              date.getFullYear().toString(),
              (date.getMonth() + 1).toString().padStart(2, '0'),
              date.getDate().toString().padStart(2, '0')
            );
          }
        });
      }
    }

    // Check if all search words match at least one field
    return searchWords.every(word =>
      searchFields.some(field => field.includes(word))
    );
  });
};

/**
 * Gets bitrate optimization recommendations for video uploads
 * @param {number} currentBitrateKbps - Current video bitrate in kbps
 * @param {number} width - Video width
 * @param {number} height - Video height
 * @param {number} duration - Video duration in seconds
 * @returns {object} - Optimization recommendations
 */
export const getBitrateOptimizationRecommendations = (currentBitrateKbps, width, height, duration) => {
  const fileSizeEstimate = (currentBitrateKbps * duration) / (8 * 1024); // MB

  // Recommended bitrates based on resolution (Nexxen-friendly)
  const recommendations = {
    '720p': { min: 1500, max: 3000, optimal: 2000 },
    '1080p': { min: 2000, max: 4000, optimal: 3000 },
    '1440p': { min: 3000, max: 6000, optimal: 4500 },
  };

  let resolution = 'custom';
  let targetBitrate = 2000; // Default safe bitrate

  if (width <= 1280 && height <= 720) {
    resolution = '720p';
    targetBitrate = recommendations['720p'].optimal;
  } else if (width <= 1920 && height <= 1080) {
    resolution = '1080p';
    targetBitrate = recommendations['1080p'].optimal;
  } else if (width <= 2560 && height <= 1440) {
    resolution = '1440p';
    targetBitrate = recommendations['1440p'].optimal;
  }

  const targetFileSizeMB = (targetBitrate * duration) / (8 * 1024);
  const compressionRatio = currentBitrateKbps / targetBitrate;

  return {
    currentBitrate: Math.round(currentBitrateKbps),
    recommendedBitrate: targetBitrate,
    resolution: resolution,
    currentFileSizeEstimate: Math.round(fileSizeEstimate * 10) / 10,
    targetFileSizeEstimate: Math.round(targetFileSizeMB * 10) / 10,
    compressionNeeded: compressionRatio > 1.5,
    compressionRatio: Math.round(compressionRatio * 100) / 100,
    recommendations: [
      `Target bitrate: ${targetBitrate} kbps for ${resolution}`,
      `Expected file size after compression: ~${Math.round(targetFileSizeMB * 10) / 10}MB`,
      compressionRatio > 2 ? '🔥 Significant compression recommended for reliable uploads' :
      compressionRatio > 1.5 ? '📉 Moderate compression recommended' : '✅ Bitrate is acceptable'
    ]
  };
};

/**
 * Validates video codec (simple browser-based check)
 * @param {File} file - Video file
 * @returns {Promise<{isValid: boolean, codec: string, message: string}>}
 */
export const validateVideoCodec = (file) => {
  return new Promise((resolve) => {
    const videoElement = document.createElement("video");
    videoElement.src = URL.createObjectURL(file);

    videoElement.onloadedmetadata = function() {
      const canPlayH264 = videoElement.canPlayType('video/mp4; codecs="avc1.42E01E"');
      const isH264 = (canPlayH264 === 'probably' || canPlayH264 === 'maybe');

      URL.revokeObjectURL(videoElement.src);

      if (isH264) {
        resolve({
          isValid: true,
          codec: 'H.264',
          message: '✅ H.264 codec detected'
        });
      } else {
        resolve({
          isValid: false,
          codec: 'Unknown',
          message: '⚠️ Unable to confirm H.264 codec. H.264 required - video may be rejected.'
        });
      }
    };

    videoElement.onerror = function() {
      URL.revokeObjectURL(videoElement.src);
      resolve({
        isValid: false,
        codec: 'Unknown',
        message: '❌ Unable to detect video codec. File may be corrupted.'
      });
    };
  });
};

/**
 * Constants for media validation
 */
export const MEDIA_CONSTANTS = {
  ALLOWED_IMAGE_TYPES: ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/avif", "image/webp"],
  ALLOWED_VIDEO_TYPES: ["video/mp4"],
  MAX_FILE_SIZE_MB: 30, // Matches Nexxen CTV specs (5MB-30MB recommended)
  VIDEO_DURATION_MIN: 6,
  VIDEO_DURATION_MAX: 30,
  S3_URL_EXPIRES: 600, // 10 minutes in seconds
  BULK_UPLOAD_CHUNK_SIZE: 10,
  // Platform bitrate requirements
  BITRATE_LIMITS: {
    MIN_ALLOWED: 800,
    MAX_ALLOWED: 5000,
    WARNING_THRESHOLD: 4000,
    OPTIMAL_720P: 2000,
    OPTIMAL_1080P: 3000,
    RECOMMENDED: 3000,
  },
};

export default {
  transformFileName,
  getFileNameWithoutExtension,
  uploadToS3Storage,
  getVideoDuration,
  getVideoDimensions,
  getVideoMetadata,
  getImageDimensions,
  validateFileSize,
  validateImageDimensions,
  validateVideoDuration,
  validateVideoBitrate,
  processMediasWithUrls,
  filterAdGroupsByMediaType,
  filterAdGroupsByMediaCompatibility,
  capitalizeFirstLetter,
  formatFileSize,
  handleMediaError,
  handleMediaSuccess,
  debounce,
  performCreativeSearch,
  getBitrateOptimizationRecommendations,
  MEDIA_CONSTANTS,
};
